// @ts-nocheck
import {
  Box,
  Button,
  Icon,
  IconButton,
  Input,
  InputAdornment,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowBack, ArrowForward, Close, Search, Save, Info } from "@mui/icons-material";
import { useDashboardFilters } from "../../customHooks/useDashboardFilters";
import {
  extractFiltersFromState,
  applyFiltersToState,
  hasFiltersChanged,
  compareObjects,
} from "../../utils/dashboardFiltersUtils";

import SortArrow from "../../assets/images/sort-arrow.svg";
import Show from "../../assets/images/Show.svg";

import AddPlus from "../../assets/images/add-plus.svg";
import styled from "@emotion/styled";
import { NewPreBookModal } from "./_components/NewPreBook";
import { fetchPrebookContractorsAction } from "../../store/actions/event/prebook";
import LoadingOverlay from "../../components/Custom/LoadingOverlay";
import { getZebraRowSx } from "../../components/Table/zebraStripe";
import ResizeHandle from "../../components/Table/ResizeHandle";
import { widthLockSx } from "../../components/Table/widthLockSx";
import { useColumnWidths } from "../../customHooks/useColumnWidths";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getUniversalDateTimeFormat } from "../../utils/methods/universalDateTimeFormat";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import LineClamp from "../positions/components/LineClamp";
import { ViewPreBookModal } from "./_components/ViewPreBook";
import { inputRegex } from "../../utils/constants/inputRegex";
import GenericTransparentBtn from "../../components/Custom/GenericTransparentBtn";
import { onSubmitFormNoAction } from "../../utils/methods/eventUtils";
import { ClearAllTextButton } from "../../components/Filter/ClearAllTextButton";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& th": {
    backgroundColor: "#D175B6 !important",
    padding: "2px 15px",
    fontSize: "20px",
    color: "#ffffff",
    borderColor: "#D175B6",
    "& span": {
      color: "#ffffff !important"
    }
  },
  "& th:not(:last-child)": {
    paddingRight: "0 !important"
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  },
  borderBottom: "1px solid",
  textAlign: "center",
  borderColor: getCssVariableHandler({ variableName: "--primary-pink-hex" })
}));

const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
  "&.MuiPaginationItem-root": {
    color: theme.palette.common.white
  },
  "&.Mui-selected": {
    color: theme.palette.secondary.main
  }
}));

const initialData = {
  search: "",
  sortOrder: "",
  sortField: "",
  dateRange: {
    startDate: "",
    endDate: "",
  },
  searchFilters: [],
  rowsPerPage: 10,
};

export const PreBookContractors = () => {
  const dispatch = useDispatch();
  const { prebookList, isLoading, currentPage, totalPages, searchString } =
    useSelector((state) => state?.prebookContractors);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [searchText, setSearchText] = useState(searchString || "");
  const [viewPreBook, setViewPreBook] = useState({
    state: false,
    data: null
  });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(currentPage || 1);
  const [filterParams, setFilterParams] = useState({
    sortOrder: "",
    sortField: ""
  });
  const { widths, setWidth, resetWidth } = useColumnWidths(
    "preBookContractorsColumnWidths.v1"
  );
  const [preBookModal, setPreBookModal] = useState({
    state: false
  });
  // const initialRef = useRef(false);
  const [isFilterInitialised, setIsFilterInitialised] = useState(false);
  const skipNextFetchRef = useRef(false);

  // ====== PRE-BOOK CONTRACTORS FILTERS MANAGEMENT ======
  const {
    originalFilters,
    savedFilters,
    isSaving,
    isDeleting,
    isLoading: isLoadingFilters,
    saveFilters,
    loadFilters,
    deleteFilters,
    updateFilters,
  } = useDashboardFilters("pre-book-contractors-dashboard");

  const [isFilterDataUpdated, setIsFilterDataUpdated] = useState(
    !compareObjects(
      originalFilters?.data?.filters,
      savedFilters?.data?.filters,
    ),
  );

  useEffect(() => {
    if (originalFilters) {
      setIsFilterDataUpdated(
        !compareObjects(
          originalFilters?.data?.filters,
          savedFilters?.data?.filters,
        ),
      );
    } else {
      setIsFilterDataUpdated(
        !compareObjects(initialData, savedFilters?.data?.filters),
      );
    }
  }, [savedFilters, originalFilters]);

  // Manual save filters handler (called by Save button)
  const handleSavePreBookFilters = () => {
    const currentFilters = extractFiltersFromState({
      filterParams: { ...filterParams, search: searchText },
      filterDates: {},
      searchBy: [],
      rowsPerPage: 10, // Fixed limit in fetchPageData
    });

    saveFilters(
      "pre-book-contractors-dashboard",
      currentFilters,
      () => {},
      (error) => {
        console.error("Error saving pre-book contractors filters:", error);
      },
    );
  };

  // Function to clear saved filters
  const clearSavedPreBookFiltersHandler = () => {
    const clearedFilterParams = {
      sortOrder: "",
      sortField: "",
    };

    setFilterParams(clearedFilterParams);
    setSearchText("");
    setPage(1);

    deleteFilters(
      "pre-book-contractors-dashboard",
      () => undefined,
      (error) => console.error("Error clearing pre-book contractors filters:", error),
    );

    skipNextFetchRef.current = true;
    dispatch(
      fetchPrebookContractorsAction({
        payload: {
          params: {
            holdDownShift: "preBook",
            limit: 10,
            page: 1,
            search: "",
            ...clearedFilterParams,
          },
        },
      }),
    );
  };
  // ====== END PRE-BOOK CONTRACTORS FILTERS MANAGEMENT ======

  const handleSearch = useCallback(
    (e) => {
      const value = e?.target?.value?.replace(inputRegex, "");
      setSearchText(value);
    },
    [setSearchText]
  );

  useEffect(() => {
    setRows(prebookList);
  }, [prebookList]);

  // Load filters on component mount
  useEffect(() => {
    if (prebookList?.length > 0 || totalPages === 0) {
      setIsFilterInitialised(true);
      return;
    }
    
    loadFilters(
      "pre-book-contractors-dashboard",
      (savedFiltersData) => {
        const { data } = savedFiltersData || {};
        if (!data || (Object.keys(data).length === 0 && !data.filters)) {
          setIsFilterInitialised(true);
        }
      },
      (error) => {
        setIsFilterInitialised(true);
      },
    );
  }, []);

  // Watch for changes in savedFilters from Redux store
  useEffect(() => {
    const { data } = savedFilters || {};

    if (data && (data?.filters || Object.keys(data).length > 0)) {
      const appliedFilters = applyFiltersToState(data);

      if (appliedFilters) {
        // Apply filter params
        setFilterParams((prev) => ({
          ...prev,
          sortOrder: appliedFilters.filterParams?.sortOrder || prev.sortOrder,
          sortField: appliedFilters.filterParams?.sortField || prev.sortField,
        }));

        // Apply search filters
        if (appliedFilters.filterParams?.search) {
          setSearchText(appliedFilters.filterParams?.search || "");
        }
      }
      setIsFilterInitialised(true);
    }
  }, [savedFilters]);

  // Consolidated effect for fetching table data
  useEffect(() => {
    // Only proceed if filters have been initialised
    if (!isFilterInitialised) return;

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const currentFilters = extractFiltersFromState({
      filterParams: { ...filterParams, search: searchText },
      filterDates: {},
      searchBy: [],
      rowsPerPage: 10,
    });

    const savedData = savedFilters?.data;
    // Skip API call only if:
    // 1. Dashboard already has data, AND
    // 2. Filters are initialized, AND
    // 3. Filters haven't changed, AND
    // 4. Page matches the currentPage in Redux (pagination triggers when page !== currentPage)
    const isPageUnchanged = page === currentPage;

    // Optional optimization: skip if data exists and filters match
    if (
      (!!prebookList.length || totalPages == 0) &&
      isFilterInitialised &&
      !hasFiltersChanged(currentFilters, savedData) &&
      isPageUnchanged
    ) {
      return;
    }

    // Use a small debounce to catch multiple state updates at once (e.g. from saved filters)
    const timeout = setTimeout(() => {
      fetchPageData();

      if (!isFilterInitialised) return;

      const currentFilters = extractFiltersFromState({
        filterParams: { ...filterParams, search: searchText },
        filterDates: {},
        searchBy: [],
        rowsPerPage: 10,
      });

      const savedData = savedFilters?.data;

      // Only update Redux if the filters have actually changed
      if (hasFiltersChanged(currentFilters, savedData)) {
        updateFilters("pre-book-contractors-dashboard", {
          data: { filters: currentFilters },
        });
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    isFilterInitialised,
    page,
    filterParams?.sortField,
    filterParams?.sortOrder,
    searchText
  ]);

  // Handle page reset when filters change
  useEffect(() => {
    if (!isFilterInitialised) return;
    if (page !== 1) {
      setPage(1);
    }
  }, [
    filterParams?.sortField, 
    filterParams?.sortOrder, 
    searchText
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const getContractorNames = useCallback((list) => {
    return [
      ...new Set(
        list?.map(
          (item) =>
            `${item?.contractorDetails?.firstName} ${item?.contractorDetails?.lastName}`
        )
      )
    ];
  }, []);

  const getUniqueContractors = useCallback((list) => {
    const cons = new Set();
    const filterList = new Set();
    list?.forEach((item) => {
      if (!cons?.has(item?.contractorDetails?.email)) {
        cons.add(item?.contractorDetails?.email);
        filterList.add(item?.contractorDetails);
      }
    });

    return Array.from(filterList);
  }, []);

  const getFormattedDates = useCallback((list) => {
    if (!list?.length) return { dates: [], selectedDates: [] };

    // Step 1: Extract, format, deduplicate, and sort in one pass
    const uniqueSortedDates = [
      ...new Set(
        list.map(({ date }) =>
          dayjs(date?.split("T")?.[0]).format("YYYY-MM-DD")
        )
      )
    ].sort();

    // Step 2: Merge consecutive dates
    const dateRanges = uniqueSortedDates
      .reduce((ranges, current, index, array) => {
        if (index === 0) {
          ranges.push([current]); // Initialize first range
          return ranges;
        }

        const prev = array[index - 1];
        if (dayjs(prev).add(1, "day").format("YYYY-MM-DD") === current) {
          ranges[ranges.length - 1].push(current); // Extend range
        } else {
          ranges.push([current]); // Start new range
        }

        return ranges;
      }, [])
      .map((range) =>
        range.length > 1
          ? `${getUniversalDateTimeFormat({
              dateTime: range[0]
            })} - ${getUniversalDateTimeFormat({ dateTime: range.at(-1) })}`
          : getUniversalDateTimeFormat({ dateTime: range[0] })
      );

    return {
      dates: dateRanges, // Merged date ranges
      selectedDates: uniqueSortedDates // Individual unique dates
    };
  }, []);

  function fetchPageData() {
    if (!isFilterInitialised) return;
    dispatch(
      fetchPrebookContractorsAction({
        payload: {
          params: {
            holdDownShift: "preBook",
            limit: 10,
            page,
            search: searchText,
            ...(filterParams || {})
          }
        }
      })
    );
  }

  const handleSortRequest = ({ cb, field }) => {
    const newOrder = filterParams?.sortOrder === "asc" ? "desc" : "asc";
    setFilterParams((filterParams) => {
      return {
        ...filterParams,
        sortOrder: newOrder,
        sortField: field
      };
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          padding: "20px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          "@media (max-width: 1000px)": {
            flexDirection: "column",
            padding: "20px 20px"
          }
        }}
      >
        <Typography
          variant={isSmallScreen ? "h3" : "h1"}
          sx={{
            "@media (max-width: 600px)": {
              marginBottom: "15px"
            }
          }}
        >
          Pre Book Contractors
        </Typography>
        <form autoComplete="off" onSubmit={onSubmitFormNoAction}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              "& .MuiInputBase-root": {
                background: "rgba(0, 0, 0, 0.01) !important",
                boxShadow:
                  "0px 0px 30px 0px rgba(209, 117, 182, 0.20) !important",
                borderRadius: "8px",
                padding: "5px 24px 5px 7px",
                "&:before": {
                  content: "none"
                },
                "&:after": {
                  content: "none"
                },
                "& svg": {
                  color: "#ffffff"
                },
                "& input": {
                  color: "#ffffff"
                }
              },
              "@media (max-width: 1000px)": {
                width: "100%"
              }
            }}
          >
            <Input
              id="input-with-icon-adornment"
              value={searchText}
              onChange={handleSearch}
              placeholder="Search here"
              sx={{
                paddingLeft: "1rem !important",
                border: "1px solid #FFF !important",
                "@media (max-width: 1000px)": {
                  flexGrow: "1"
                  // width: "70%"
                }
              }}
              // startAdornmentendAdornment={
              //   <InputAdornment position="start">
              //     <Search />
              //   </InputAdornment>
              // }
              endAdornment={
                <InputAdornment position="start">
                  {!searchText ? (
                    <Search
                      sx={{
                        fontSize: "1.25rem"
                        // marginRight: "0.7rem"
                      }}
                    />
                  ) : (
                    <GenericTransparentBtn
                      onClick={() => {
                        setSearchText("");
                      }}
                      style={{
                        background: "grey",
                        padding: "0.10rem",
                        borderRadius: "50%",
                        onHover: {
                          background: "grey",
                          padding: "0.10rem"
                        }
                      }}
                    >
                      <Close
                        sx={{
                          fontSize: "1rem"
                          // marginRight: "0.7rem"
                        }}
                      />
                    </GenericTransparentBtn>
                  )}
                </InputAdornment>
              }
            />
            {
              <Button
                sx={{
                  marginLeft: "10px",
                  borderRadius: "4px",
                  border: "1px solid #FFF",
                  background:
                    "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%) !important",
                  boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
                  minWidth: "auto",
                  padding: "5px !important",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                }}
                onClick={() => {
                  // NOTE: Add pre book contractor
                  setPreBookModal({ state: true });
                }}
              >
                <img src={AddPlus} />
              </Button>
            }
            <Tooltip title="Save Filters">
              <Button
                onClick={handleSavePreBookFilters}
                disabled={isSaving}
                sx={{
                  marginLeft: "10px",
                  borderRadius: "4px",
                  border: "1px solid #FFF",
                  background:
                    "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%) !important",
                  boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
                  minWidth: "auto",
                  padding: "5px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} sx={{ color: "#FFF" }} />
                ) : (
                  <Save sx={{ color: "#FFF" }} />
                )}
              </Button>
            </Tooltip>

            {/* Currently Applied Filters */}
            <Tooltip
              title={
                <Box
                  sx={{
                    maxWidth: 400,
                    maxHeight: 600,
                    overflowY: "auto",
                    p: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Currently Applied Filters
                  </Typography>
                  {/* un saved filters */}
                  {!!isFilterDataUpdated && (
                    <>
                      <Typography
                        variant="caption"
                        fontWeight="300"
                        color="warning.main"
                        mb={1}
                      >
                        You have unsaved filter changes
                      </Typography>
                    </>
                  )}
                  <Divider color="#8F00FF" sx={{ mb: 1 }} />

                  {/* Search */}
                  <Typography variant="caption" fontWeight="bold">
                    Search
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    {searchText || "None"}
                  </Typography>

                  {/* Sort */}
                  <Typography variant="caption" fontWeight="bold">
                    Sort
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    {filterParams?.sortField
                      ? `${filterParams.sortField} (${filterParams.sortOrder})`
                      : "None"}
                  </Typography>

                  <Divider color="#8F00FF" sx={{ my: 1 }} />

                  {/* Clear */}
                  <Box display="flex" justifyContent="center">
                    <ClearAllTextButton
                      onClear={clearSavedPreBookFiltersHandler}
                    />
                  </Box>
                </Box>
              }
            >
              <IconButton
                sx={{
                  marginLeft: "10px",
                  borderRadius: "4px",
                  border: "1px solid #FFF",
                  background:
                    "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%) !important",
                  boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
                  minWidth: "auto",
                  padding: "5px !important",
                  width: "40px",
                  height: "40px",
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "white" }}
                  />
                ) : (
                  <Info sx={{ color: "#FFF" }} />
                )}
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                maxWidth: "100%"
              }}
            ></Box>
          </Box>
        </form>
      </Box>
      {isLoading || prebookList !== rows ? (
        <LoadingOverlay open={true} />
      ) : (
        <Box>
          <TableContainer
            className="custom-scroll-design"
            sx={{
              maxWidth: "100%",
              ...(isSmallScreen
                ? {
                    minHeight: "calc(100vh - 323px)"
                  }
                : {
                    height: "88vh"
                  }),
              overscrollBehavior: "auto"
            }}
          >
            <Table aria-label="sticky table" stickyHeader>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell
                    sx={{ position: "relative", ...widthLockSx(widths["bookedDate"]) }}
                  >
                    <TableSortLabel
                      active={true}
                      direction={
                        filterParams?.sortField === "date"
                          ? filterParams?.sortOrder
                          : "desc"
                      }
                      onClick={() =>
                        handleSortRequest({
                          field: "date"
                        })
                      }
                      IconComponent={(props) => (
                        <IconButton {...props}>
                          <Icon>{<img src={SortArrow} />}</Icon>
                        </IconButton>
                      )}
                    >
                      Booked Date
                    </TableSortLabel>
                    <ResizeHandle
                      onResize={(px) => setWidth("bookedDate", px)}
                      onReset={() => resetWidth("bookedDate")}
                    />
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      textWrap: "nowrap",
                      position: "relative",
                      ...widthLockSx(widths["contractorName"])
                    }}
                  >
                    <TableSortLabel
                      active={true}
                      direction={
                        filterParams?.sortField === "name"
                          ? filterParams?.sortOrder
                          : "desc"
                      }
                      onClick={() =>
                        handleSortRequest({
                          field: "name"
                        })
                      }
                      IconComponent={(props) => (
                        <IconButton {...props}>
                          <Icon>{<img src={SortArrow} />}</Icon>
                        </IconButton>
                      )}
                    >
                      Contractor Name
                    </TableSortLabel>
                    <ResizeHandle
                      onResize={(px) => setWidth("contractorName", px)}
                      onReset={() => resetWidth("contractorName")}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ position: "relative", ...widthLockSx(widths["comment"]) }}
                  >
                    Comment
                    <ResizeHandle
                      onResize={(px) => setWidth("comment", px)}
                      onReset={() => resetWidth("comment")}
                    />
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      textWrap: "nowrap",
                      position: "relative",
                      ...widthLockSx(widths["prebookedOn"])
                    }}
                  >
                    PreBooked on
                    <ResizeHandle
                      onResize={(px) => setWidth("prebookedOn", px)}
                      onReset={() => resetWidth("prebookedOn")}
                    />
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      textWrap: "nowrap",
                      position: "relative",
                      ...widthLockSx(widths["prebookedBy"])
                    }}
                  >
                    PreBooked by
                    <ResizeHandle
                      onResize={(px) => setWidth("prebookedBy", px)}
                      onReset={() => resetWidth("prebookedBy")}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {rows?.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={10}>
                      <Typography sx={{ textAlign: "center" }}>
                        No Records Found
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  rows?.map((item, i) => {
                    const conNames = getContractorNames(item?.bookings);
                    const { dates, selectedDates } = getFormattedDates(
                      item?.bookings
                    );
                    return (
                      <StyledTableRow key={i} sx={getZebraRowSx(i)}>
                        <StyledTableCell
                          sx={{
                            position: "relative",
                            paddingLeft: "3.5rem",
                            ...widthLockSx(widths["bookedDate"])
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              setViewPreBook({
                                state: true,
                                data: {
                                  selectedDates: selectedDates,
                                  contractors: getUniqueContractors(
                                    item?.bookings
                                  ),
                                  comment: item?.bookings?.[0]?.comment
                                }
                              });
                            }}
                            sx={{
                              position: "absolute",
                              left: "5px",
                              top: "50%",
                              transform: "translateY(-50%)"
                            }}
                          >
                            <img src={Show} />
                          </IconButton>
                          <Typography
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              "& a": { lineHeight: "0" }
                            }}
                          >
                            {dates.slice(0, 4).map((date, index) => (
                              <Typography key={index}>
                                {date}
                                {index + 1 < dates.slice(0, 4).length
                                  ? ","
                                  : null}
                              </Typography>
                            ))}
                            {dates.length > 4 && (
                              <Typography>
                                ...{dates.length - 4} more date
                                {dates.length - 4 > 1 ? "s" : ""}
                              </Typography>
                            )}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell sx={widthLockSx(widths["contractorName"])}>
                          <Typography
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              "& a": { lineHeight: "0" }
                            }}
                          >
                            {conNames.slice(0, 4).map((contractor, index) => (
                              <Typography key={index}>{contractor}</Typography>
                            ))}
                            {conNames?.length > 4 && (
                              <Typography>
                                ...{conNames?.length - 4} more contractor
                                {conNames?.length - 4 > 1 ? "s" : ""}
                              </Typography>
                            )}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell sx={widthLockSx(widths["comment"])}>
                          <Tooltip title={item?.bookings?.[0]?.comment}>
                            <Typography sx={{ fontSize: "1rem" }}>
                              <LineClamp
                                text={item?.bookings?.[0]?.comment}
                                maxLines={1}
                              />
                            </Typography>
                          </Tooltip>
                        </StyledTableCell>

                        <StyledTableCell sx={widthLockSx(widths["prebookedOn"])}>
                          <Typography>
                            {dayjs(item?.bookings?.[0]?.createdAt).format(
                              "ddd MM/DD/YYYY [at] hh:mm a"
                            )}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell sx={widthLockSx(widths["prebookedBy"])}>
                          <Typography>
                            {item?.bookings?.[0]?.bookedByDetails?.fullName}
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              "& td": {
                display: "block"
              },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "black",
              borderRadius: "0px 0px 15px 15px",
              borderTop: "1px solid #D175B6"
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handleChangePage}
              siblingCount={isSmallScreen ? 0 : 1}
              renderItem={(item) => (
                <StyledPaginationItem
                  slots={{ previous: ArrowBack, next: ArrowForward }}
                  {...item}
                />
              )}
            />
          </Box>
        </Box>
      )}
      <NewPreBookModal
        open={preBookModal?.state}
        handleClose={() => setPreBookModal({ state: false })}
        onUpdate={fetchPageData}
      />
      <ViewPreBookModal
        open={viewPreBook?.state}
        data={viewPreBook?.data}
        handleClose={() => setViewPreBook({ state: false, data: null })}
      />
    </LocalizationProvider>
  );
};